import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface Crumb {
    label: ReactNode
    href?: string
}

interface PageHeaderProps {
    crumbs?: Crumb[]
    eyebrow?: ReactNode
    title: ReactNode
    subtitle?: ReactNode
    description?: ReactNode
    children?: ReactNode
    className?: string
}

export const Breadcrumbs: FC<{ items: Crumb[] }> = ({ items }): ReactNode => {
    return (
        <nav aria-label='Breadcrumb' className='mb-5'>
            <ol className='flex flex-wrap items-center gap-1 text-sm text-muted-foreground'>
                {items.map((item, index) => (
                    <li key={index} className='flex min-w-0 items-center gap-1'>
                        {index > 0 && <ChevronRight className='size-4 shrink-0' aria-hidden='true' />}

                        {item.href ? (
                            <Link href={item.href} className='truncate font-medium hover:text-foreground hover:underline'>
                                {item.label}
                            </Link>
                        ) : (
                            <span className='truncate font-semibold text-foreground' aria-current='page'>
                                {item.label}
                            </span>
                        )}
                    </li>
                ))}
            </ol>
        </nav>
    )
}

const PageHeader: FC<PageHeaderProps> = ({ crumbs, eyebrow, title, subtitle, description, children, className }): ReactNode => {
    return (
        <section className={cn('border-b-[3px] border-foreground bg-dots', className)}>
            <div className='mx-auto max-w-6xl px-4 py-10 sm:px-6 md:py-14'>
                {crumbs && crumbs.length > 0 && <Breadcrumbs items={crumbs} />}

                {eyebrow && <div className='mb-4'>{eyebrow}</div>}

                <h1 className='max-w-4xl text-3xl font-black leading-tight sm:text-4xl md:text-5xl'>
                    {title}
                </h1>

                {subtitle && (
                    <div className='mt-2 text-lg font-semibold text-muted-foreground'>
                        {subtitle}
                    </div>
                )}

                {description && (
                    <div className='mt-4 max-w-3xl text-base leading-relaxed text-muted-foreground md:text-lg'>
                        {description}
                    </div>
                )}

                {children && <div className='mt-6'>{children}</div>}
            </div>
        </section>
    )
}

export default PageHeader
